import {
  stringField,
  stringArrayField,
  recordArrayField,
  recordField,
  booleanField,
  positiveIntField,
  schemaField,
  schemaArrayField,
} from "./fieldTypes";
import { toEitherEnum, toDollarAmount, toDate } from "./valueTransformers";

import {
  AccessRecord,
  AgencyRecord,
  AgencySlugRecord,
  ClientRecord,
  ClientSlugRecord,
  CredentialsProvider,
  CredentialsProviderRecord,
  PaymentSourceRecord,
  PaymentStatus,
  ProfileRecord,
  RefundType,
  SettingsPointOfSaleRecord,
  RoleCombinationRecord,
  Status,
  UserRole,
} from "@/sanctuary/typedefs";

export const createSchema = (fields = [], transformers = []) => ({
  fields,
  transformers,
});

export const USER = createSchema(
  [
    stringField("user_id", "id"),
    stringField("role"),
    stringField("pos_role"),
    stringField("email"),
    recordField(ClientRecord)("client"),
    recordArrayField(AgencyRecord)("agencies"),
    stringArrayField("permissions"),
    booleanField("active", "isActive"),
    stringField("status"),
    stringField("statusLabel"),
    stringField("first_name", "firstName"),
    stringField("last_name", "lastName"),
    stringField("fullNameFormatted"), // This isn't technically in the response. It is appended afterward to simplify Users sorting.
    stringField("timezone"),
    stringField("credentialsProvider"),
  ],
  [toEitherEnum(UserRole)("role"), toEitherEnum(UserRole)("pos_role")]
);

export const OKTA_USER = createSchema(
  [
    stringField("id"),
    stringField("status"),
    stringField("status_label", "statusLabel"),
    recordField(ProfileRecord)("profile"),
    recordField(ClientSlugRecord)("client"),
    recordField(CredentialsProviderRecord)(
      "credentials_provider",
      "credentialsProvider"
    ),
    recordArrayField(AgencySlugRecord)("agencies"),
    recordArrayField(AccessRecord)("access"),
  ],
  [
    toEitherEnum(Status)("status"),
    toEitherEnum(CredentialsProvider)("credentialsProvider"),
  ]
);

export const CLIENTS = createSchema([recordArrayField(ClientRecord)("clients")]);

export const DASHBOARD = createSchema([stringField("url")]);

export const OKTA_PASSWORD_RESET_URL = createSchema([
  stringField("reset_password_url", "resetPasswordUrl"),
]);

const CREDIT_CARD = createSchema([
  stringField("card_type", "type"),
  stringField("first_six", "firstSix"),
  stringField("last_four", "lastFour"),
  stringField("exp_month", "expMonth"),
  stringField("exp_year", "expYear"),
  stringField("cardholder_name", "cardholderName"),
  stringField("zip_code", "zipCode"),
]);

const BANK_ACCOUNT = createSchema([
  stringField("account_holder_name", "bankAccountHolderName"),
  stringField("account_number_last_four", "lastFour"),
  stringField("bank_account_type", "type"),
]);

const WIRE_PAYMENT = createSchema([
  stringField("effective_date", "effectiveDate"),
  stringField("description"),
  stringField("name"),
]);

const PAYMENT_ACCOUNT = createSchema([stringField("number")]);

const SPECIFIC_PARAMS = createSchema([
  stringField("email_address", "emailAddress"),
  stringField("reversal_reason", "reversalReason"),
]);
const CUSTOM_ATTRIBUTES = createSchema([stringField("value"), stringField("key")]);

const LINE_ITEMS = createSchema([
  positiveIntField("id"),
  positiveIntField("amount_in_cents", "amountInCents"),
  positiveIntField("amount_refunded_in_cents", "amountRefundedInCents"),
  positiveIntField("amount_refundable_in_cents", "amountRefundableInCents"),
  schemaArrayField(CUSTOM_ATTRIBUTES)("custom_attributes", "customAttributes"),
]);

const ASSOCIATED_PAYMENTS = createSchema(
  [
    positiveIntField("id"),
    stringField("created_at", "createdAt"),
    stringField("status"),
    stringField("amount", "amount"),
    stringField("amount_refunded", "amountRefunded"),
    stringField("payment_process_type_name", "paymentProcessTypeName"),
    schemaArrayField(LINE_ITEMS)("line_items", "lineItems"),
  ],
  [toDollarAmount("amount"), toDollarAmount("amountRefunded"), toDate("createdAt")]
);

export const PAYMENT = createSchema(
  [
    positiveIntField("id"),
    stringField("status"),
    booleanField("voidable"),
    booleanField("refundable"),
    stringField("refund_type_allowed", "refundTypeAllowed"),
    stringField("service_type", "serviceType"),
    stringField("amount", "subtotal"),
    stringField("amount_refundable", "amountRefundable"),
    stringField("amount_refunded", "amountRefunded"),
    stringField("total_amount", "totalAmount"),
    stringField("service_fee", "serviceFee"),
    stringField("funded_at", "fundedAt"),
    stringField("created_at", "createdAt"),
    stringField("agency"),
    stringField("payment_process_type_name", "paymentProcessTypeName"),
    stringField("payment_type", "type"),
    stringField("payment_initiator", "paymentInitiator"),
    stringField("obligation_slug", "obligationSlug"),
    stringField("check_image_url_front", "checkImageUrlFront"),
    stringField("check_image_url_back", "checkImageUrlBack"),
    positiveIntField("original_payment_id", "originalPaymentId"),
    recordField(PaymentSourceRecord)("payment_source", "paymentSource"),
    schemaField(CREDIT_CARD)("credit_card", "creditCard"),
    schemaField(BANK_ACCOUNT)("bank_account", "bankAccount"),
    schemaField(WIRE_PAYMENT)("wire_payment", "wirePayment"),
    schemaField(PAYMENT_ACCOUNT)("account"),
    schemaField(SPECIFIC_PARAMS)("specific_params", "specificParams"),
    schemaArrayField(LINE_ITEMS)("line_items", "lineItems"),
    schemaArrayField(ASSOCIATED_PAYMENTS)(
      "associated_payments",
      "associatedPayments"
    ),
  ],
  [
    toEitherEnum(PaymentStatus)("status"),
    toEitherEnum(RefundType)("refundTypeAllowed"),
    toDollarAmount("subtotal"),
    toDollarAmount("amountRefundable"),
    toDollarAmount("amountRefunded"),
    toDollarAmount("totalAmount"),
    toDollarAmount("serviceFee"),
    toDate("createdAt"),
  ]
);

export const PAYMENTS_SEARCH = createSchema([
  positiveIntField("total"),
  schemaArrayField(PAYMENT)("transactions"),
]);

export const CUSTOMER = createSchema([
  stringField("first_name", "firstName"),
  stringField("last_name", "lastName"),
  stringField("email"),
  stringField("created_at", "createdAt"),
  stringField("id"),
  stringField("type"),
  stringField("status"),
  stringField("title"),
  stringField("timezone"),
]);

export const CUSTOMER_META = createSchema([
  positiveIntField("total_results", "totalResults"),
  positiveIntField("total_pages", "totalPages"),
  positiveIntField("page_size", "pageSize"),
  positiveIntField("page_number", "pageNumber"),
]);

const SERVICE_TYPE = createSchema([stringField("name"), stringField("description")]);

const PAYMENT_SOURCE = createSchema([stringField("name")]);

const AGENCY = createSchema([
  stringField("name"),
  recordField(ClientRecord)("client"),
  schemaArrayField(SERVICE_TYPE)("service_types", "serviceTypes"),
  schemaArrayField(PAYMENT_SOURCE)("payment_sources", "paymentSources"),
]);

export const AGENCIES = createSchema([schemaArrayField(AGENCY)("agencies")]);

const OKTA_PAGE_CURSOR = createSchema([
  stringField("page_cursor", "pageCursor"),
  positiveIntField("page_size", "pageSize"),
]);

export const ADMINISTERED_OKTA_USERS = createSchema([
  schemaArrayField(OKTA_USER)("data", "users"),
  schemaField(OKTA_PAGE_CURSOR)("meta"),
]);

const PAYMENT_ATTRIBUTES = createSchema([
  stringArrayField("description"),
  stringArrayField("subDescription"),
]);

const OBLIGATIONS = createSchema([
  stringField("obligationSlug"),
  schemaField(PAYMENT_ATTRIBUTES)("paymentAttributes"),
]);

export const SETTINGS_PROFILE_CONFIG = createSchema([
  schemaArrayField(OBLIGATIONS)("obligations"),
  booleanField("customerManagementEnabled"),
]);

export const SETTINGS_ROOT_CONFIG = createSchema([
  recordField(SettingsPointOfSaleRecord)("pointOfSale"),
]);

export const ROLE_COMBINATIONS_CONFIG = createSchema([
  recordField(RoleCombinationRecord)("data"),
]);

export const META_CONFIG = createSchema([stringField("paymentPortalUrl")]);

export const EMPTY = createSchema();
